import React, { useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import LanguageSelector from '../components/LanguageSelector';
import { FileText, AlertCircle } from 'lucide-react';
import './LoginPage.css';

function LoginPage() {
  const { t } = useTranslation('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await login(email, password);
      const heading = location.state?.from;
      navigate(heading ? `${heading.pathname}${heading.search || ''}` : '/');
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (detail?.error_code === 'email_not_verified') {
        navigate('/register', { state: { email: detail.email, step: 'verify', password } });
      } else if (typeof detail === 'string') {
        setError(detail);
      } else {
        setError(t('errors.generic'));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-page login-page--split">
      <div className="login-language-selector">
        <LanguageSelector />
      </div>
      <div className="login-split">
        <aside className="login-brand">
          <span className="login-brand-logo"><span>C</span>hatsControl</span>
          <h2 className="login-brand-headline">{t('brand.headline')}</h2>
          <ul className="login-brand-points">
            <li>{t('brand.point1')}</li>
            <li>{t('brand.point2')}</li>
            <li>{t('brand.point3')}</li>
          </ul>
        </aside>
        <div className="login-container">
        <div className="login-header">
          <FileText size={40} className="login-icon" />
          <h1>{t('title')}</h1>
          <p>{t('subtitle')}</p>
        </div>

        {error && (
          <div className="error-message">
            <AlertCircle size={16} />
            <span>{error}</span>
          </div>
        )}

        <form onSubmit={handleSubmit} className="login-form">
          <div className="form-group">
            <label htmlFor="email">{t('form.email')}</label>
            <input
              id="email"
              type="email"
              className="input"
              placeholder={t('form.emailPlaceholder')}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="password">{t('form.password')}</label>
            <input
              id="password"
              type="password"
              className="input"
              placeholder={t('form.passwordPlaceholder')}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
            <Link to="/forgot-password" state={{ email }} className="login-forgot-link" data-testid="forgot-password-link">
              {t('forgotLink')}
            </Link>
          </div>

          <button
            type="submit"
            className="btn btn-primary submit-btn"
            disabled={loading}
          >
            {loading ? (
              <>
                <div className="spinner" />
                {t('form.signingIn')}
              </>
            ) : (
              t('form.signIn')
            )}
          </button>
        </form>

        <div className="login-register-hint">
          {t('noAccount')} <Link to="/register">{t('createAccount')}</Link>
        </div>
        </div>
      </div>
    </div>
  );
}

export default LoginPage;
